/**
 * The chart of the prices by the time of sale of a real estate estimate as PHP prepares it (SalesHorizonChartData):
 * the same numbers as the picture of the documents, the chart only draws them.
 */
export type OfferType = 'sale' | 'lease';

export interface SaleModeColumn {
    name: string;
    price: number;
    pricePerMeter: number;
    probability: number;
    numberOfEstates: number;
    // The days in words, "do 30 dní", "31–90 dní", "od 91 dní", as PHP writes them for the documents (SaleModeDays)
    days: string;
}

export interface SalesHorizonChartData {
    saleModes: SaleModeColumn[];
    estimatedPrice: number;
    estimatedPricePerMeter: number;
    offerType: OfferType;
}

export interface SummaryItem {
    label: string;
    value: string;
    note: string;
}

export const SALES_HORIZON_COLORS = {
    column: '#509bfe',
    estimate: '#e26e2c',
    grid: 'rgba(0, 0, 0, 0.06)',
    muted: '#6c757d',
    ink: '#212529',
};

const money = new Intl.NumberFormat('cs-CZ', {style: 'currency', currency: 'CZK', maximumFractionDigits: 0});
const decimal = new Intl.NumberFormat('cs-CZ', {minimumFractionDigits: 1, maximumFractionDigits: 1});

export function formatMoney(amount: number): string {
    return money.format(amount);
}

export function formatPerMeter(amount: number): string {
    return `${money.format(amount)}/m²`;
}

export function formatProbability(probability: number): string {
    return `${decimal.format(probability)} %`;
}

/** "12 prodejů", of a lease "12 pronájmů" */
export function formatSales(sales: number, offerType: OfferType): string {
    const [one, few, many] = offerType === 'lease' ? ['pronájem', 'pronájmy', 'pronájmů'] : ['prodej', 'prodeje', 'prodejů'];

    if (sales === 1) {
        return `1 ${one}`;
    }

    return `${sales} ${sales >= 2 && sales <= 4 ? few : many}`;
}

/** The days of the sale mode in words, the text of PHP (SaleModeDays) */
export function formatDays(saleMode: SaleModeColumn): string {
    return saleMode.days;
}

/** "6,35 mil." of the axis of prices */
export function formatAxisPrice(amount: number): string {
    if (Math.abs(amount) >= 1_000_000) {
        return `${new Intl.NumberFormat('cs-CZ', {maximumFractionDigits: 2}).format(amount / 1_000_000)} mil.`;
    }

    return `${new Intl.NumberFormat('cs-CZ', {maximumFractionDigits: 0}).format(amount / 1000)} tis.`;
}

/** The relative difference of a price from the estimated one, "+3,0 %" */
export function differenceFromEstimate(price: number, estimate: number): string {
    const difference = (price - estimate) / estimate * 100;
    const sign = difference > 0 ? '+' : (difference < 0 ? '−' : '');

    return `${sign}${decimal.format(Math.abs(difference))} %`;
}

/**
 * Above the chart: the estimated price, the fastest sale and the slowest one with their difference from the estimate
 */
export function summaryItems(chart: SalesHorizonChartData): SummaryItem[] {
    const offer = chart.offerType === 'lease' ? 'Pronájem' : 'Prodej';
    const items: SummaryItem[] = [{
        label: 'Odhadní cena',
        value: formatMoney(chart.estimatedPrice),
        note: formatPerMeter(chart.estimatedPricePerMeter),
    }];

    const first = chart.saleModes[0];
    const last = chart.saleModes[chart.saleModes.length - 1];

    if (first !== undefined) {
        items.push({label: `${offer} ${formatDays(first)}`, value: formatMoney(first.price), note: `${differenceFromEstimate(first.price, chart.estimatedPrice)} proti odhadu`});
    }

    if (last !== undefined && last !== first) {
        items.push({label: `${offer} ${formatDays(last)}`, value: formatMoney(last.price), note: `${differenceFromEstimate(last.price, chart.estimatedPrice)} proti odhadu`});
    }

    return items;
}

// The ticks of the axis of prices of the picture of the documents (GdSalesHorizonChartRenderer)
const MAXIMUM_PRICE_TICKS = 5;

/**
 * The bounds of the axis of prices as the picture of the documents counts them (PriceAxis::of()): the prices with
 * a padding, not from zero, on a step of 1, 2 or 5 times a power of ten, at most 5 ticks
 */
export function priceBounds(chart: SalesHorizonChartData): {min: number; max: number; step: number} {
    const prices = [...chart.saleModes.map(saleMode => saleMode.price), chart.estimatedPrice];
    const lowest = Math.min(...prices);
    const highest = Math.max(...prices);
    const padding = Math.max((highest - lowest) * 0.15, highest * 0.03);
    const low = lowest - padding;
    const high = highest + padding;
    const spaces = MAXIMUM_PRICE_TICKS - 1;
    let step = niceStep((high - low) / spaces);
    const count = Math.ceil(high / step) - Math.floor(low / step);

    if (count > spaces) {
        step = niceStep(count * step / spaces);
    }

    return {min: Math.floor(low / step) * step, max: Math.ceil(high / step) * step, step};
}

function niceStep(range: number): number {
    const magnitude = 10 ** Math.floor(Math.log10(range));
    const fraction = range / magnitude;
    const nice = fraction <= 1 ? 1 : (fraction <= 2 ? 2 : (fraction <= 5 ? 5 : 10));

    return nice * magnitude;
}
